/**
 * Check if audio is enabled via URL parameter
 */
function isAudioEnabled(): boolean {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('audio') === '1';
}

/**
 * Check if winter theme is enabled via URL parameter
 */
function isWinterThemeEnabled(): boolean {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('winter') === '1';
}

/**
 * Sound manager for playing game audio
 */
export class SoundManager {
  private static audioEnabled = isAudioEnabled();
  private static winterEnabled = isWinterThemeEnabled();
  private static sounds: Map<string, HTMLAudioElement> = new Map();

  /**
   * Preload a sound file
   */
  static preload(name: string, path: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const audio = new Audio(path);
      audio.preload = 'auto';
      
      audio.addEventListener('canplaythrough', () => {
        this.sounds.set(name, audio);
        resolve();
      }, { once: true });

      audio.addEventListener('error', (e) => {
        console.error(`Failed to load sound: ${name}`, e);
        reject(e);
      });

      audio.load();
    });
  }

  /**
   * Play a sound if audio is enabled
   * Automatically uses winter variant if winter theme is enabled and variant exists
   */
  static play(name: string): void {
    if (!this.audioEnabled) {
      return;
    }

    // Try winter variant first if winter theme is enabled
    let soundName = name;
    if (this.winterEnabled) {
      const winterName = `${name}_winter`;
      if (this.sounds.has(winterName)) {
        soundName = winterName;
      }
    }

    const sound = this.sounds.get(soundName);
    if (sound) {
      // Clone the audio node to allow overlapping playback
      const clone = sound.cloneNode() as HTMLAudioElement;
      clone.volume = sound.volume;
      clone.play().catch((error) => {
        console.warn(`Failed to play sound: ${soundName}`, error);
      });
    } else {
      console.warn(`Sound not found: ${soundName}`);
    }
  }

  /**
   * Set volume for a specific sound (0.0 to 1.0)
   */
  static setVolume(name: string, volume: number): void {
    const sound = this.sounds.get(name);
    if (sound) {
      sound.volume = Math.max(0, Math.min(1, volume));
    }
  }
}

